/**
 * Start the Amazon Ads (LWA) grant in a popup and receive its result.
 *
 * The popup callback page relays the grant back to this window. It is accepted
 * once, only from our own origin, only from the popup this hook opened, and
 * only after schema validation — everything downstream (which Shops exist,
 * which seller is authorized next) is derived from it.
 */
import type { SpApiRegion } from "@adpilot/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { AmazonAdsGrant } from "@/lib/amazon-ads-oauth";
import { parseTrustedAmazonAdsGrant } from "@/lib/amazon-ads-oauth";
import { fetchAmazonConnectUrl } from "@/lib/amazon-api";
import { showPopupStatus } from "@/lib/oauth-popup-status";

const POPUP_NAME = "amazon-connect";
const POPUP_FEATURES = "width=600,height=700";

export interface AmazonAdsConnectController {
  /** Sign in through the given region's Amazon account system. */
  connect: (region: SpApiRegion) => void;
  starting: boolean;
}

export function useAmazonAdsConnect(
  onGrant: (grant: AmazonAdsGrant) => void,
): AmazonAdsConnectController {
  const { t } = useTranslation("auth");
  const [starting, setStarting] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const succeededRef = useRef(false);
  const onGrantRef = useRef(onGrant);
  onGrantRef.current = onGrant;

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (succeededRef.current) return;
      const grant = parseTrustedAmazonAdsGrant(event, window.location.origin, popupRef.current);
      if (!grant) return;
      succeededRef.current = true;
      onGrantRef.current(grant);
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const connect = useCallback(
    async (region: SpApiRegion) => {
      // Open the popup inside the click, before any await: browsers (Safari above
      // all) block a window opened after the click's activation has lapsed. There
      // is no full-page fallback — the popup callback page has no opener to hand
      // the grant to, so it would strand the seller there.
      const popup = window.open("", POPUP_NAME, POPUP_FEATURES);
      if (!popup) {
        toast.error(t("binding.popupBlocked"));
        return;
      }
      popupRef.current = popup;
      showPopupStatus(popup, {
        title: t("binding.popup.openingTitle"),
        message: t("binding.popup.openingMessage"),
      });
      setStarting(true);
      try {
        const { redirectUrl } = await fetchAmazonConnectUrl("popup", region);
        popup.location.replace(redirectUrl);
      } catch {
        // Left in the popup in case the browser will not let this page close it.
        showPopupStatus(popup, {
          title: t("binding.popup.failedTitle"),
          message: t("binding.startFailed"),
          hint: t("binding.popup.closeHint"),
        });
        popup.close();
        popupRef.current = null;
        toast.error(t("binding.startFailed"));
      } finally {
        setStarting(false);
      }
    },
    [t],
  );

  return { connect: (region) => void connect(region), starting };
}
